import { useMemo, useState } from "react";
import { ArrowLeft, DownloadIcon, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { ColumnDef } from "@tanstack/react-table";
import type { KeyedMutator } from "swr";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MaskBadge } from "@/components/ui/mask-badge";
import { MasterVersionBadge } from "@/components/ui/master-version-spec-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GenericTable } from "@/components/shared/GenericTable";
import { useDownloadMasterSpec, useSpecList } from "@/hooks/useSpecLists";
import { RawSpecList, SpecList, SpecListVersion, SpecValue } from "@/types/specList";
import { NewSpecListVersionDialog } from "./NewSpecListVersionDialog";
import { SpecListDetailsTableToolbar } from "./SpecListDetailsTableToolbar";
import { SpecDetailsSidePanel } from "./SpecDetailsSidePanel";
import { GenericErrorDisplay } from "../shared/ErrorDisplay";
import { LoadingDisplay } from "../shared/LoadingDisplay";
import { NotFoundDisplay } from "../shared/NotFoundDisplay";
import { addPreviousValuesToSpecValues } from "./utils";
import Tooltip from "../shared/Tooltip";
import { WarningAlert } from "../shared/Alerts";
import { CreateMetaData } from "../shared/CreateMetaData";
import { Main } from "../shared/Main";
import { Header } from "../shared/Header";

type RefetchSpecList = KeyedMutator<RawSpecList>;

type SpecificationsTableProps = {
  conditions: string[];
  deviceClasses: string[];
  version: SpecListVersion;
  onSpecClick: (spec: SpecValue) => void;
  hasMasterVersion: boolean;
};

const SpecificationsTable = ({
  conditions,
  deviceClasses,
  version,
  onSpecClick,
}: SpecificationsTableProps) => {
  const columns: ColumnDef<SpecValue>[] = [
    {
      id: "name",
      accessorKey: "spec.name",
      header: "Spec Name",
    },
    {
      id: "condition",
      accessorKey: "spec.condition",
      header: "Condition",
      enableGlobalFilter: false,
      cell: ({ row }) => <Badge variant="outline">{row.original.spec.condition}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "reportName",
      accessorKey: "spec.reportName",
      header: "Report Name",
    },
    {
      id: "legacyNames",
      accessorKey: "spec.legacyNames",
      header: "Legacy Names",
    },
    {
      id: "DUT_names",
      accessorKey: "spec.dutNames",
      header: "DUT Name",
    },
    {
      id: "deviceClass",
      accessorKey: "spec.deviceClass",
      header: "Device Class",
      cell: ({ row }) => <Badge variant="outline">{row.original.spec.deviceClass}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "recipe",
      accessorKey: "spec.recipe.name",
      header: "Recipe",
    },
  ];

  const addRowClassNames = (data: SpecValue) => {
    return data.isMasterValue ? "" : "bg-orange-50/50";
  };

  const handleRowClick = (row: SpecValue) => {
    onSpecClick(row);
  };

  return (
    <GenericTable
      addRowClassNames={addRowClassNames}
      columns={columns}
      data={version.specValues}
      enableRowSelection={false}
      onRowClick={handleRowClick}
      toolbarComponent={(props) => (
        <SpecListDetailsTableToolbar
          {...props}
          conditions={conditions}
          deviceClasses={deviceClasses}
        />
      )}
    />
  );
};

type SpecListVersionDetailsProps = {
  refetch: RefetchSpecList;
  selectedVersion: string;
  specList: SpecList;
  version: SpecListVersion;
};
const SpecListVersionDetails = ({
  refetch,
  selectedVersion,
  specList,
  version,
}: SpecListVersionDetailsProps) => {
  const navigate = useNavigate();

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState<SpecValue | null>(null);
  const downloadMasterSpec = useDownloadMasterSpec();

  const isLatestVersion = version.id === specList.latestVersion.id;
  const canDownload = specList.type === "master" && isLatestVersion && version.uploaded;

  // Apply previousValues for display purposes
  const versionWithPreviousValues = useMemo(() => {
    return {
      ...version,
      specValues: addPreviousValuesToSpecValues(
        specList.masterVersion?.specValues ?? [],
        version.specValues,
      ),
    };
  }, [version, specList.masterVersion?.specValues]);

  const deviceClasses = [
    ...new Set(versionWithPreviousValues.specValues.map((sp) => sp.spec.deviceClass)),
  ];
  const conditions = [
    ...new Set(versionWithPreviousValues.specValues.map((sp) => sp.spec.condition ?? "None")),
  ];

  const onSpecListVersionChange = (newVersion: string) => {
    navigate(`/spec-lists/${specList.id}/version/${newVersion}`);
  };

  const handleSpecClick = (spec: SpecValue) => {
    setSelectedSpec(spec);
  };

  const handleCloseSidePanel = () => {
    setSelectedSpec(null);
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-6">
          <Tooltip
            asChild
            content="Go back"
            trigger={
              <Button variant="outline" size="icon" onClick={goBack}>
                <ArrowLeft />
              </Button>
            }
            side="right"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold">{specList.name}</h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-2">
                    Mask: <MaskBadge maskName={specList.mask?.name ?? "-"} />
                  </span>
                  {/* Only show Master Version for derived spec lists */}
                  {specList.masterVersion && (
                    <span className="flex items-center gap-2">
                      Master Version:{" "}
                      <MasterVersionBadge
                        masterVersionId={specList.masterVersion.id}
                        masterId={specList.masterVersion.specList.id}
                        masterName={`${specList.masterVersion.specList.name} - v${specList.masterVersion.version}`}
                      />
                    </span>
                  )}
                  <span className={"flex items-center gap-2"}>
                    Status:
                    {specList.type === "derived" ? <Badge variant="outline">Derived</Badge> : null}
                    {specList.type === "master" ? <Badge variant="default">Master</Badge> : null}
                    {specList.type === "custom" ? <Badge variant="outline">Custom</Badge> : null}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex">
              <Select value={selectedVersion} onValueChange={onSpecListVersionChange}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Version" />
                </SelectTrigger>
                <SelectContent>
                  {specList.versions.reverse().map((version) => (
                    <SelectItem key={version.id} value={version.id}>
                      v{version.version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Version Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Version {version.version}</span>
                <CreateMetaData createdAt={version.createdAt} createdBy={version.createdBy} />
              </CardTitle>
            </CardHeader>
          </Card>

          {/* Main Content */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Specifications</h2>
              {specList.masterVersion && (
                <p className="text-sm text-muted-foreground mt-1">
                  Rows with orange backgrounds indicate specs that have changed since Master
                  Version. Click on a row to view details.
                </p>
              )}
            </div>
            <div className="flex gap-2 items-end">
              {canDownload && (
                <Tooltip
                  asChild
                  trigger={
                    <Button variant="outline" onClick={() => downloadMasterSpec(specList.id)}>
                      <DownloadIcon />
                    </Button>
                  }
                  content="Download master spec file"
                />
              )}
              {specList.isArchived || specList.masterVersion?.specList.isArchived ? (
                <WarningAlert className={"flex justify-center h-9 items-center self-end"}>
                  {specList.isArchived
                    ? "This spec list is archived and cannot be modified."
                    : "This spec list cannot be edited because its master spec is archived."}
                </WarningAlert>
              ) : (
                <Button onClick={() => setShowSaveDialog(true)}>
                  <Save className="mr-2" />
                  Create New Version
                </Button>
              )}
            </div>
          </div>

          <Card>
            <CardContent>
              <SpecificationsTable
                conditions={conditions}
                deviceClasses={deviceClasses}
                hasMasterVersion={!!specList.masterVersion}
                onSpecClick={handleSpecClick}
                version={versionWithPreviousValues}
              />
            </CardContent>
          </Card>

          {showSaveDialog ? (
            <NewSpecListVersionDialog
              currentVersion={version}
              maskId={specList.mask?.id}
              masterVersion={specList.masterVersion}
              onOpenChange={setShowSaveDialog}
              open
              refetch={refetch}
              specList={specList}
            />
          ) : null}
        </div>

        {/* Side Panel - Outside the relative container */}
        {selectedSpec && (
          <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/50 z-40" onClick={handleCloseSidePanel} />
            <SpecDetailsSidePanel specValue={selectedSpec} onClose={handleCloseSidePanel} />
          </>
        )}
      </Main>
    </>
  );
};

export const SpecListVersionDetailsWrapper = ({
  refetch,
  specList,
  specListVersionId,
}: {
  refetch: RefetchSpecList;
  specList: SpecList;
  specListVersionId: string;
}) => {
  const selectedSpecListVersion = specList.versions.find((v) => v.id === specListVersionId);

  if (!selectedSpecListVersion)
    return <NotFoundDisplay redirectUrl="/spec-lists" text="Spec List Version not found" />;

  return (
    <SpecListVersionDetails
      refetch={refetch}
      selectedVersion={specListVersionId}
      specList={specList}
      version={selectedSpecListVersion}
    />
  );
};

export function SpecListDetails() {
  const { id, versionId } = useParams();

  const specListId = id as string;
  const specListVersionId = versionId as string;

  const {
    data: specList,
    isLoading: isSpecListLoading,
    error: isSpecListError,
    mutate: refetchSpecList,
  } = useSpecList(specListId);

  if (isSpecListLoading) {
    return <LoadingDisplay />;
  }

  if (isSpecListError) {
    return <GenericErrorDisplay />;
  }

  if (!specList) {
    return <NotFoundDisplay redirectUrl="/spec-lists" text="Spec List not found" />;
  }

  return (
    <SpecListVersionDetailsWrapper
      specList={specList}
      specListVersionId={specListVersionId}
      refetch={refetchSpecList}
    />
  );
}
